/*
 * GA4 product-list tracking for the sale grids.
 *
 * Answers two questions the store's other analytics cannot: how far down the grid shoppers
 * actually get, and which positions turn into clicks and orders.
 *
 * Every card carries its own data-ga-* attributes because the infinite scroll appends only
 * the <ul> children — a JSON blob printed alongside the list would be dropped from page two
 * onward. A MutationObserver picks up those appended cards.
 *
 * Impressions are batched: a grid of forty products would otherwise fire forty separate
 * events as the shopper scrolls.
 */
(() => {
  const BATCH_MS = 700;
  const BATCH_MAX = 20;

  // gtag and GTM both end up in GA4. Sending through both would count everything twice, so
  // gtag wins where it exists and the dataLayer is only a fallback.
  const send = (name, params) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ecommerce: params });
  };

  const itemFrom = (el) => {
    const item = {
      item_id: el.dataset.gaItemId || '',
      item_name: el.dataset.gaItemName || '',
      index: parseInt(el.dataset.gaIndex, 10) || 0,
    };
    const price = parseFloat(el.dataset.gaPrice);
    if (!Number.isNaN(price)) item.price = price;
    if (el.dataset.gaVariantId) item.item_variant = el.dataset.gaVariantId;
    return item;
  };

  const setup = (list) => {
    if (list.dataset.gaBound === 'true') return;
    list.dataset.gaBound = 'true';

    const listId = list.dataset.gaListId || 'sale-list';
    const listName = list.dataset.gaListName || listId;
    const withList = (item) => Object.assign({}, item, { item_list_id: listId, item_list_name: listName });

    let queue = [];
    let flushTimer = null;

    const flush = () => {
      if (flushTimer) clearTimeout(flushTimer);
      flushTimer = null;
      if (!queue.length) return;
      const items = queue.splice(0, queue.length);
      send('view_item_list', { item_list_id: listId, item_list_name: listName, items });
    };

    const enqueue = (item) => {
      queue.push(withList(item));
      if (queue.length >= BATCH_MAX) {
        flush();
        return;
      }
      if (!flushTimer) flushTimer = setTimeout(flush, BATCH_MS);
    };

    // Anything still queued when the shopper leaves would otherwise never be sent.
    addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
    addEventListener('pagehide', flush);

    let observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            entry.target.dataset.gaSeen = 'true';
            enqueue(itemFrom(entry.target));
          });
        },
        { threshold: 0.5 }
      );
    }

    const watch = (card) => {
      if (!card.dataset.gaItemId || card.dataset.gaSeen === 'true' || card.dataset.gaWatched === 'true') return;
      card.dataset.gaWatched = 'true';
      // Without IntersectionObserver every card counts as seen; a slightly generous number
      // beats no list data at all on those browsers.
      if (!observer) {
        card.dataset.gaSeen = 'true';
        enqueue(itemFrom(card));
        return;
      }
      observer.observe(card);
    };

    const scan = () => list.querySelectorAll('li[data-ga-item-id]').forEach(watch);
    scan();

    // The infinite scroll appends cards straight into this list.
    new MutationObserver((records) => {
      let added = false;
      records.forEach((record) => {
        if (record.addedNodes.length) added = true;
      });
      if (added) scan();
    }).observe(list, { childList: true });

    // Delegated so appended cards need no extra wiring.
    list.addEventListener(
      'click',
      (event) => {
        const card = event.target.closest('li[data-ga-item-id]');
        if (!card || !list.contains(card)) return;
        send('select_item', {
          item_list_id: listId,
          item_list_name: listName,
          items: [withList(itemFrom(card))],
        });
      },
      { passive: true }
    );
  };

  const start = () => document.querySelectorAll('ul[data-ga-list-id]').forEach(setup);

  // Analytics should never compete with the grid for the first paint.
  if ('requestIdleCallback' in window) {
    requestIdleCallback(start, { timeout: 3000 });
  } else {
    setTimeout(start, 1200);
  }
})();
